/* eslint-disable react-hooks/exhaustive-deps */
// node-modules
import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Common } from "../../../common/src/index";
import { config } from "../../config/config";
import "../../locales/i18n";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
  UseSnackbarContext,
} from "../../pages/ProjectDetail";
import { PointCloudType } from "../../viewer/pointcloud/loader/interface";
import FormatUtil from "../Utility/ProjectComUtil";
import CoordinateContext from "./Contexts/CoordinateContext";
import { CoordinateTXT } from "./CoordinateTXT";
import { CurrentModeTXT } from "./CurrentModeTXT";
import { Pix4DTXT } from "./Pix4DTXT";
import { RTKIcon } from "./RTKIcon";

// fetchが実行されているか確認する変数
let whileFetching: boolean = false;
let loadTrajectoryAbortController = new AbortController();

interface Props {}

/* Viewerコンポネント */
const PointCloudViewer = forwardRef((props: Props, ref: Ref<any>) => {
  const {
    projectDetail,
    displayType,
    viewer,
    whilePointCloudLoading,
    updateWhilePointCloudLoading,
    detailTab,
    currentViewerMode,
    updateCurrentPointCloud,
  } = useContext(ProjectDetailContext);
  const { openError } = useContext(UseSnackbarContext);
  const { t } = useTranslation();
  // const [key] = useState(0);
  const canvasElement = useRef<HTMLCanvasElement>(
    document.createElement("canvas")
  ); //Container
  const [isPointcloudStreaming, setIsPointcloudStreaming] =
    useState<boolean>(true); // streaming
  // UI

  // calculation
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const [hasLoadedPointCloud, setHasLoadedPointCloud] = useState(false);

  const { fetchCS } = useContext(CoordinateContext);

  /*****************************************
   Effect
  ******************************************/
  // init
  useEffect(() => {
    viewer.Init(canvasElement.current);
    document.addEventListener("pointloaded", (e) => {
      updateCurrentPointCloud((e as CustomEvent).detail);
    });
    if (displayType === EDisplayType.Web) {
      const ua = navigator.userAgent;
      if (ua.indexOf("Windows") > 0) {
        viewer.setMaxIterations(100);
      } else {
        viewer.setMaxIterations(40);
      }
    } else {
      viewer.setMaxIterations(40);
    }
    return () => {
      viewer.abortLoadPointCloud();
    };
  }, []);

  useEffect(() => {
    projectDetail && fetchCS(projectDetail.id);
  }, [projectDetail.id]);

  // 点群切替：表示→削除(停止)
  useEffect(() => {
    viewer.setCurrentType(DETAIL_TAB2POINT_CLOUD_TYPE(detailTab));
    setHasLoadedPointCloud(false);
    loadPointCloud();
    const controller = abortControllerRef.current;
    return () => {
      controller.abort();
      abortControllerRef.current = new AbortController();
      // 処理停止
      viewer.abortLoadPointCloud();
      loadTrajectoryAbortController.abort();
      // 表示削除
      viewer.clear();
    };
  }, [detailTab]);

  useEffect(() => {
    return () => {
      if (currentViewerMode === EViewerMode.POINT_REMOVE) {
        viewer.abortLoadPointCloud();
        loadPointCloud();
      }
    };
  }, [currentViewerMode]);

  useImperativeHandle(ref, () => ({
    reloadPointCloud() {
      viewer.abortLoadPointCloud();
      loadPointCloud();
    },
  }));

  const loadPointCloud = async () => {
    viewer.unloadPhotoshots();

    try {
      updateWhilePointCloudLoading(true);
      whileFetching = true;
      let abortError = false;
      const url = await axios
        .get(
          `${config.serverHost}/api/v1/project/${
            projectDetail?.id
          }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/base`,
          { withCredentials: true, signal: abortControllerRef.current.signal }
        )
        .then((res) => {
          return res.data;
        })
        .catch((e) => {
          if (e?.message === "canceled") {
            abortError = true;
          } else {
            return "no_url";
          }
        });
      if (abortError) return;
      if (url === "no_url") {
        if (
          displayType === EDisplayType.iOS &&
          DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) !==
            Common.POINT_CLOUD_TYPE.SIMPLE
        ) {
          whileFetching = false;
        } else {
          await loadRawPointCloud();
          whileFetching = false;
        }
      } else {
        const timeStamp = await axios
          .get(
            `${config.serverHost}/api/v1/project/${
              projectDetail?.id
            }/point-cloud/${FormatUtil.reqStringForPointCloud(
              DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)
            )}/removal`,
            { withCredentials: true }
          )
          .then((res) => {
            return res.data.time;
          })
          .catch((err) => {
            console.error(err);
          });
        setIsPointcloudStreaming(true);
        await viewer.loadPointCloud(
          DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) as string,
          projectDetail?.id,
          timeStamp,
          { type: PointCloudType.STREAM, url: url, version: 1, gridSize: 0.5 }
        );
      }
      if (
        DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) ===
        Common.POINT_CLOUD_TYPE.SIMPLE
      ) {
        const catchLogUrl = `${config.serverHost}/api/v1/project/${projectDetail?.id}/rtkFix`;
        viewer.loadPhotoshots(catchLogUrl);
      }
    } catch (e) {
      console.error(e);
    } finally {
      updateWhilePointCloudLoading(false);
    }
  };

  const loadRawPointCloud = async () => {
    updateWhilePointCloudLoading(true);
    setIsPointcloudStreaming(false);
    const urlRaw = `${config.serverHost}/api/v1/project/${
      projectDetail?.id
    }/point-cloud/${
      displayType === EDisplayType.Web ? "Web" : "IOS"
    }?type=${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}`;
    return await viewer
      .loadPointCloud(
        DETAIL_TAB2POINT_CLOUD_TYPE(detailTab),
        projectDetail?.id,
        new Date().toISOString(),
        {
          type:
            DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) ===
            Common.POINT_CLOUD_TYPE.SIMPLE
              ? PointCloudType.PLY
              : PointCloudType.LAS,
          url: urlRaw,
          version: 1,
        }
      )
      .then(() => {
        setHasLoadedPointCloud(true);
      })
      .catch(() => {
        if (!whileFetching) {
          openError(t("DOWNLOAD_FAILED"));
        }
        setHasLoadedPointCloud(false);
      })
      .finally(() => {
        updateWhilePointCloudLoading(false);
      });
  };

  return (
    <Box
      sx={{
        flex: "1",
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <CurrentModeTXT height="24px" sx={{ position: "absolute" }} />
      <canvas
        id="point-view3"
        ref={canvasElement}
        style={{ height: "100%", width: "100%" }}
      />
      <CoordinateTXT />
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "rgba(0, 0, 0, 0)",
          right: "0px",
          bottom: "0px",
          padding: "4px",
          color: "#808080",
          alignItems: "right",
        }}
      >
        <RTKIcon isRTK={projectDetail?.useViDoc} />
        <Pix4DTXT />
      </Box>
      {whilePointCloudLoading && (
        <CircularProgress
          sx={{ position: "absolute", left: "50%", top: "50%" }}
        />
      )}
    </Box>
  );
});
export default PointCloudViewer;
